import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  ChevronRight,
  Edit2,
  Trash2,
  AlertTriangle,
  X,
  ArrowLeft,
  ArrowRight,
  Truck,
  Building,
  User,
  Phone,
  Mail,
  Calendar,
  Layers,
  FileText,
  MapPin,
  CheckCircle2,
  Clock,
  Palette,
  Factory,
  RotateCcw,
  Receipt,
  Download,
  Eye,
  ExternalLink,
} from "lucide-react";
import { api, downloadDocument } from "../../lib/api";
import { money, compactMoney, badgeTone, errorMessage, normalizeStage, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import MoveBackStageModal from "./MoveBackStageModal";
import OrderHistoryTrail from "./OrderHistoryTrail";

function StatusBadge({ children, style }) {
  return <span className={`badge ${badgeTone(children)}`} style={style}>{children}</span>;
}

export default function OrderDetail({ orderId, onBack, onChange, user, clients: propClients }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState("");
  const [editingOrder, setEditingOrder] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [movingBack, setMovingBack] = useState(false);
  const canManage = isAdmin(user) || canAccessDepartment(user, ["Sales"]);

  const load = useCallback(async () => {
    try {
      const r = await api("get", `/orders/${orderId}`);
      setOrder(r.data);
    } catch (e) {
      toast.error("Could not load order");
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (!order) return <div className="content"><div className="loading-screen">Loading order…</div></div>;

  const currentStage = order.current_stage || "Order Received";
  const normalized = normalizeStage(currentStage);

  const isOrderReceived = currentStage === "Order Received";
  const isDeliveryStage =
    ["Delivery", "Delivered", "Out for Delivery", "In Transit"].includes(currentStage) ||
    ["Out for Delivery", "In Transit", "Delivered"].includes(order.delivery_status) ||
    ["Out for Delivery", "Delivered"].includes(order.dispatch_status) ||
    normalized === "Delivery";

  const isProductionStage =
    ["Production", "Dispatch", "Challan", "Delivery", "Delivered", "Invoice", "Accounts"].includes(currentStage) ||
    (order.production_status && order.production_status !== "Not Started") ||
    Boolean(order.production_vendor_name);

  const isDesignStage =
    ["Design", "Approval", "Client Approval"].includes(currentStage) ||
    Boolean(order.design_stage);

  return (
    <div className="content detail-content" data-testid="order-detail-page" style={{ maxWidth: 1280, margin: "0 auto", paddingBottom: 40 }}>
      {/* Back Button */}
      <div style={{ marginBottom: 12 }}>
        <button className="back-btn" data-testid="order-detail-back-button" onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
          <ArrowLeft size={14} /> Back to Orders
        </button>
      </div>

      {/* Detail Head Card */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 18,
          boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
          {/* Left: Identification, Title & Meta */}
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span
                style={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "var(--mono)",
                  letterSpacing: "0.5px",
                }}
              >
                ORDER / {order.order_id}
              </span>
              <span style={{ color: "#cbd5e1" }}>•</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
                {order.client_name}
              </span>
            </div>

            <h1 style={{ margin: "2px 0 4px", fontSize: 21, color: "var(--navy)", fontWeight: 700, lineHeight: 1.25 }} data-testid="order-detail-product-title">
              {order.product}
            </h1>

            {/* Order Description Directly Below Product Name - Bold, Prominent, Clean, Non-Boxed (Matching Design section) */}
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: order.description?.trim() ? "#0f172a" : "#94a3b8",
                fontStyle: order.description?.trim() ? "normal" : "italic",
                lineHeight: 1.45,
                margin: "4px 0 8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              data-testid="order-head-description-text"
            >
              {order.description?.trim() ? order.description.trim() : "No order description available."}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "#64748b" }}>
              <span>Quantity: <b style={{ color: "var(--navy)" }}>{(order.quantity || 0).toLocaleString()} units</b></span>
              <span style={{ color: "#cbd5e1" }}>•</span>
              <span>Due: <b style={{ color: "var(--navy)" }}>{order.required_delivery_date || "—"}</b></span>
              <span style={{ color: "#cbd5e1" }}>•</span>
              <span>Order Value: <b style={{ color: "var(--navy)" }}>{order.total_value && order.total_value > 0 ? compactMoney(order.total_value) : "₹0"}</b></span>
            </div>
          </div>

          {/* Right: Stage Badge & Action Buttons */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            {/* Current Stage Badge */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              data-testid="order-current-stage"
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "#64748b",
                }}
              >
                Current Stage
              </span>
              <StatusBadge
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  padding: "4px 12px",
                  borderRadius: 6,
                  letterSpacing: "0.2px",
                }}
              >
                {currentStage}
              </StatusBadge>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
              {currentStage !== "Order Received" && (
                <button
                  type="button"
                  className="outline-btn compact"
                  data-testid="order-detail-move-back-button"
                  style={{
                    color: "#b45309",
                    borderColor: "#fde68a",
                    background: "#fffbeb",
                    fontWeight: 600,
                    fontSize: 12,
                    height: 30,
                    padding: "0 10px",
                  }}
                  onClick={() => setMovingBack(true)}
                  title="Move order back to previous stage"
                >
                  <RotateCcw size={12.5} /> Move to Previous Stage
                </button>
              )}
              {canManage && (
                <button
                  type="button"
                  className="outline-btn compact"
                  data-testid="order-detail-edit-button"
                  style={{ fontSize: 12, height: 30, fontWeight: 600, padding: "0 10px" }}
                  onClick={() => setEditingOrder(true)}
                  title="Edit order"
                >
                  <Edit2 size={12.5} /> Edit
                </button>
              )}
              {isAdmin(user) && (
                <button
                  type="button"
                  className="outline-btn compact"
                  data-testid="order-detail-delete-button"
                  style={{ color: "#dc2626", borderColor: "#fca5a5", background: "#fef2f2", fontSize: 12, height: 30, fontWeight: 600, padding: "0 10px" }}
                  onClick={() => setDeletingOrder(true)}
                  title="Delete order"
                >
                  <Trash2 size={12.5} /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main 2-Column Content Grid */}
      <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 16, marginBottom: 20 }}>
        {/* Left Column */}
        <div className="stack" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Order Overview Panel */}
          <section
            className="panel"
            style={{
              background: "#ffffff",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: "16px 18px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
            data-testid="order-overview-panel"
          >
            <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Order Overview</h3>
                <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>Key order information &amp; financials</p>
              </div>
            </div>
            <div
              className="facts"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "14px 12px",
                marginTop: 8,
              }}
            >
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Current Stage</small>
                <b><StatusBadge style={{ fontSize: 11.5, padding: "2px 8px" }}>{currentStage}</StatusBadge></b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Client</small>
                <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }}>{order.client_name}</b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Order Value</small>
                <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 700 }}>{order.total_value && order.total_value > 0 ? money(order.total_value) : "₹0"}</b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Order Date</small>
                <b style={{ color: "#334155", fontSize: 13, fontWeight: 500 }}>{order.order_date || "—"}</b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Priority</small>
                <b>
                  <span className={`badge ${order.priority === "Urgent" ? "red" : order.priority === "High" ? "amber" : "blue"}`} style={{ fontSize: 11, padding: "2px 7px" }}>
                    {order.priority || "Normal"}
                  </span>
                </b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>GST (18%)</small>
                <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }}>{order.gst && order.gst > 0 ? money(order.gst) : "₹0"}</b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>PO Number</small>
                <b className="mono" style={{ color: "#334155", fontSize: 12.5 }}>{order.po_number || "—"}</b>
              </div>
              <div>
                <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Outstanding</small>
                <b style={{ color: order.outstanding_amount > 0 ? "#dc2626" : "var(--navy)", fontSize: 13, fontWeight: 700 }}>
                  {order.outstanding_amount && order.outstanding_amount > 0 ? money(order.outstanding_amount) : "₹0"}
                </b>
              </div>
              {(order.assigned_designer || order.assigned_producer) && (
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>
                    {order.assigned_designer ? "Assigned Designer" : "Assigned Specialist"}
                  </small>
                  <b style={{ color: "#0284c7", fontSize: 13, fontWeight: 600 }} data-testid="order-detail-overview-assigned">
                    {order.assigned_designer || order.assigned_producer}
                  </b>
                </div>
              )}
            </div>
          </section>

          {/* Client Information Panel */}
          {order.client_details && (
            <section
              className="panel"
              style={{
                background: "#ffffff",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "16px 18px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              }}
              data-testid="order-client-panel"
            >
              <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <h3 style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
                    <Building size={16} color="#0284c7" /> Client Information
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Contact details &amp; confirmation status
                  </p>
                </div>
              </div>
              <div
                className="facts"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "14px 12px",
                  marginTop: 8,
                }}
              >
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Company</small>
                  <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }}>{order.client_details.company_name || order.client_name || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Contact Person</small>
                  <b style={{ color: "#334155", fontSize: 13, fontWeight: 500 }}>{order.client_details.contact_person || order.client_contact_person || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Email</small>
                  <b style={{ fontSize: 13 }}>
                    {order.client_details.email ? (
                      <a href={`mailto:${order.client_details.email}`} style={{ color: "#0284c7", textDecoration: "none" }}>
                        {order.client_details.email}
                      </a>
                    ) : "—"}
                  </b>
                </div>
                {canViewClientPhone(user) && order.client_details.phone && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Phone</small>
                    <b>
                      <a href={`tel:${order.client_details.phone}`} style={{ color: "#0284c7", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                        <Phone size={12} /> {order.client_details.phone}
                      </a>
                    </b>
                  </div>
                )}
                {canViewClientPhone(user) && order.client_details.alternate_phone && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Alternate Phone</small>
                    <b>
                      <a href={`tel:${order.client_details.alternate_phone}`} style={{ color: "#0284c7", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                        <Phone size={12} /> {order.client_details.alternate_phone}
                      </a>
                    </b>
                  </div>
                )}
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Account Manager</small>
                  <b style={{ color: "#334155", fontSize: 13, fontWeight: 500 }}>{order.client_details.account_manager || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Confirmation Email</small>
                  <div>
                    {order.confirmation_email_status === "Sent" ? (
                      <span className="badge green" style={{ fontSize: 11, padding: "2px 8px" }} title={`Sent to ${order.confirmation_email_recipient || order.client_details?.email}`} data-testid="order-confirmation-email-badge">
                        ✓ Sent
                      </span>
                    ) : order.confirmation_email_status ? (
                      <span className="badge gray" style={{ fontSize: 11, padding: "2px 8px" }} title={order.confirmation_email_error || order.confirmation_email_status} data-testid="order-confirmation-email-badge">
                        {order.confirmation_email_status}
                      </span>
                    ) : (
                      <span className="badge blue" style={{ fontSize: 11, padding: "2px 8px" }} data-testid="order-confirmation-email-badge">
                        Automated
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Dedicated Order Description & Specifications Section */}
          <section
            className="panel"
            style={{
              background: "#ffffff",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: "16px 18px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
            data-testid="order-description-specifications-panel"
          >
            <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <h3 style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
                  <FileText size={16} color="#0284c7" /> Order Description &amp; Specifications
                </h3>
                <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                  Product details, design instructions &amp; specifications for #{order.order_id}
                </p>
              </div>
              {order.upload_batch_id && (
                <span
                  className="mono"
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: "#2563eb",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    padding: "2px 7px",
                    borderRadius: 4,
                  }}
                  title={`Part of Bulk Request #${order.upload_batch_id}`}
                >
                  #{order.upload_batch_id}
                </span>
              )}
            </div>

            {/* Complete Saved Order Description */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "12px 14px",
                marginBottom: (order.design_specs || order.packaging_instructions || order.notes || order.remarks) ? 10 : 0,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10.5, color: "#64748b", letterSpacing: "0.3px" }}>
                  Order Description
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                  {order.product}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13.5,
                  fontWeight: order.description?.trim() ? 600 : 400,
                  color: order.description?.trim() ? "var(--navy)" : "#94a3b8",
                  fontStyle: order.description?.trim() ? "normal" : "italic",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                data-testid="order-detail-description-text"
              >
                {order.description?.trim() ? order.description.trim() : "No order description available."}
              </p>
            </div>

            {/* Product Specifications & Packaging Details Grid */}
            {(order.design_specs || order.packaging_instructions) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: (order.design_specs && order.packaging_instructions) ? "1fr 1fr" : "1fr",
                  gap: 10,
                  marginBottom: (order.notes || order.remarks) ? 10 : 0,
                }}
              >
                {order.design_specs && (
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "10px 12px",
                    }}
                  >
                    <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 3, letterSpacing: "0.3px" }}>
                      Product &amp; Design Specifications
                    </span>
                    <p style={{ margin: 0, fontSize: 12.5, color: "#334155", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                      {order.design_specs}
                    </p>
                  </div>
                )}

                {order.packaging_instructions && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: 6,
                      padding: "10px 12px",
                    }}
                  >
                    <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#15803d", display: "block", marginBottom: 3, letterSpacing: "0.3px" }}>
                      Packaging Instructions
                    </span>
                    <p style={{ margin: 0, fontSize: 12.5, color: "#166534", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                      📦 {order.packaging_instructions}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Relevant Remarks & Instructions */}
            {(order.notes || order.remarks) && (
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  padding: "10px 12px",
                }}
              >
                <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 3, letterSpacing: "0.3px" }}>
                  Important Remarks &amp; Instructions
                </span>
                <p style={{ margin: 0, fontSize: 12.5, color: "#334155", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                  {order.notes || order.remarks}
                </p>
              </div>
            )}
          </section>
        </div>

        {/* Right Column */}
        <div className="stack" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* DELIVERY STAGE: Dedicated Delivery Details Section */}
          {isDeliveryStage && (
            <section
              className="panel"
              style={{
                background: "#ffffff",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "16px 18px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              }}
              data-testid="order-delivery-details-panel"
            >
              <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <h3 style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, color: "var(--navy)", fontSize: 15, fontWeight: 700 }}>
                    <Truck size={16} color="#0284c7" /> Delivery Details
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Logistics &amp; recipient delivery tracking
                  </p>
                </div>
                {order.delivery_status && (
                  <span className={`badge ${order.delivery_status === "Delivered" ? "green" : "blue"}`} style={{ fontSize: 11, padding: "2px 8px" }}>
                    {order.delivery_status}
                  </span>
                )}
              </div>

              <div className="facts" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px 14px", marginTop: 8 }}>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Delivery Person</small>
                  <b>
                    {order.assigned_delivery_person || order.delivery_person || order.transporter ? (
                      <span style={{ color: "var(--navy)", fontSize: 13 }}>
                        {order.assigned_delivery_person || order.delivery_person || order.transporter}
                      </span>
                    ) : (
                      <span style={{ color: "#e11d48", fontWeight: 600, fontSize: 12.5 }}>
                        Not Assigned
                      </span>
                    )}
                  </b>
                </div>

                {(order.delivery_person_phone || order.driver_phone) && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Delivery Person Phone</small>
                    <b>
                      <a
                        href={`tel:${order.delivery_person_phone || order.driver_phone}`}
                        style={{ color: "#0284c7", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}
                      >
                        <Phone size={12} /> {order.delivery_person_phone || order.driver_phone}
                      </a>
                    </b>
                  </div>
                )}

                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Delivery Status</small>
                  <b>
                    <StatusBadge style={{ fontSize: 11, padding: "2px 8px" }}>{order.delivery_status || order.dispatch_status || "Pending"}</StatusBadge>
                  </b>
                </div>

                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Delivery Date</small>
                  <b style={{ color: "#334155", fontSize: 13, fontWeight: 500 }}>{order.delivery_date || order.actual_date || order.dispatch_date || order.required_delivery_date || "—"}</b>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Delivery Address</small>
                  <b style={{ color: "#334155", fontWeight: 500, fontSize: 12.5 }}>
                    {order.shipping_address ||
                      order.client_details?.shipping_address ||
                      order.delivery_address ||
                      order.client_details?.billing_address ||
                      "—"}
                  </b>
                </div>

                {order.challan_number && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Challan Number</small>
                    <b className="mono" style={{ color: "#0284c7", fontSize: 12.5 }}>#{order.challan_number}</b>
                  </div>
                )}

                {order.tracking_number && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Tracking Number</small>
                    <b className="mono" style={{ color: "#334155", fontSize: 12.5 }}>{order.tracking_number}</b>
                  </div>
                )}

                {order.received_by && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Received By</small>
                    <b style={{ color: "var(--navy)", fontSize: 13 }}>{order.received_by}</b>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* DESIGN STAGE INFO (when in Design / Approval) */}
          {isDesignStage && !isDeliveryStage && (
            <section
              className="panel"
              style={{
                background: "#ffffff",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "16px 18px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              }}
              data-testid="order-design-status-panel"
            >
              <div className="panel-title" style={{ marginBottom: 12 }}>
                <div>
                  <h3 style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, color: "var(--navy)", fontSize: 15, fontWeight: 700 }}>
                    <Palette size={16} color="#7c3aed" /> Design Workflow Status
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Artwork progression and designer assignment
                  </p>
                </div>
              </div>
              <div className="facts" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px 14px", marginTop: 8 }}>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Design Stage</small>
                  <b>
                    <span className="badge purple" style={{ fontSize: 11, padding: "2px 7px" }}>
                      {order.design_stage || (order.current_stage === "Design" ? "New" : order.current_stage)}
                    </span>
                  </b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Assigned Designer</small>
                  <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }}>{order.assigned_designer || "Unassigned"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Approval Status</small>
                  <b>
                    <span className={`badge ${order.approval_status === "Approved" ? "green" : order.approval_status === "Not Approved" ? "red" : "amber"}`} style={{ fontSize: 11, padding: "2px 7px" }}>
                      {order.approval_status || "Pending Approval"}
                    </span>
                  </b>
                </div>
                {order.design_version > 0 && (
                  <div>
                    <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Design Version</small>
                    <b style={{ color: "#334155", fontSize: 13 }}>V{order.design_version}</b>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* PRODUCTION & PROCUREMENT PANEL (when in Production / Dispatch / Delivery) */}
          {isProductionStage && (
            <section
              className="panel"
              style={{
                background: "#ffffff",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "16px 18px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              }}
              data-testid="order-production-procurement-panel"
            >
              <div className="panel-title" style={{ marginBottom: 12 }}>
                <div>
                  <h3 style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, color: "var(--navy)", fontSize: 15, fontWeight: 700 }}>
                    <Factory size={16} color="#4f46e5" /> Production &amp; Procurement
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Assigned vendor, warehouse location, and manufacturing tracking
                  </p>
                </div>
              </div>
              <div className="facts" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px 14px", marginTop: 8 }}>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Production Vendor</small>
                  <b style={{ color: "#4f46e5", fontSize: 13, fontWeight: 600 }}>{order.production_vendor_name || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Assigned Production Specialist</small>
                  <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }} data-testid="order-detail-assigned-producer">{order.assigned_producer || "Unassigned"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Warehouse / Location</small>
                  <b style={{ color: "var(--navy)", fontSize: 13, fontWeight: 600 }}>{order.warehouse_name || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Production Start</small>
                  <b style={{ color: "#334155", fontSize: 13, fontWeight: 500 }}>{order.production_start_date || "—"}</b>
                </div>
                <div>
                  <small style={{ display: "block", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>Production Status</small>
                  <b>
                    <StatusBadge style={{ fontSize: 11, padding: "2px 8px" }}>{order.production_status || "Not Started"}</StatusBadge>
                  </b>
                </div>
              </div>
            </section>
          )}

          {/* Accounts & Financial Documents Panel */}
          <section
            className="panel"
            style={{
              background: "#ffffff",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: "16px 18px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
            data-testid="order-accounts-lifecycle-panel"
          >
            <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Receipt size={16} color="#16a34a" />
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Accounts &amp; Financial Documents</h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Proforma Invoice, Challan &amp; Final Invoice status
                  </p>
                </div>
              </div>

              {order.client_id && (
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ color: "#0284c7", borderColor: "#bae6fd", background: "#f0f9ff", fontSize: 11.5, height: 26, padding: "0 8px" }}
                  onClick={() => {
                    window.history.pushState({}, "", `/accounts/client/${encodeURIComponent(order.client_id)}`);
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  title="Open Client Accounts"
                  data-testid="order-open-accounts-btn"
                >
                  <ExternalLink size={11} /> Open in Accounts
                </button>
              )}
            </div>

            <div className="facts" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px 12px", marginTop: 8 }}>
              {/* PI */}
              <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                <small style={{ display: "block", color: "#64748b", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>
                  Proforma Invoice (PI)
                </small>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>
                  {order.pi_number ? (
                    <span style={{ color: "#b45309" }}>#{order.pi_number}</span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>Pending</span>
                  )}
                </div>
                {order.pi_document_id && (
                  <button
                    className="link-btn"
                    style={{ fontSize: 10.5, marginTop: 4, color: "#0284c7", display: "inline-flex", alignItems: "center", gap: 3 }}
                    onClick={() => window.open(downloadDocument(order.pi_document_id, true), "_blank")}
                  >
                    <Eye size={10} /> View Document
                  </button>
                )}
              </div>

              {/* Challan */}
              <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                <small style={{ display: "block", color: "#64748b", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>
                  Delivery Challan
                </small>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>
                  {order.challan_number ? (
                    <span style={{ color: "#7e22ce" }}>#{order.challan_number}</span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>Pending</span>
                  )}
                </div>
                {order.challan_document_id && (
                  <button
                    className="link-btn"
                    style={{ fontSize: 10.5, marginTop: 4, color: "#0284c7", display: "inline-flex", alignItems: "center", gap: 3 }}
                    onClick={() => window.open(downloadDocument(order.challan_document_id, true), "_blank")}
                  >
                    <Eye size={10} /> View Document
                  </button>
                )}
              </div>

              {/* Final Invoice */}
              <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                <small style={{ display: "block", color: "#64748b", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>
                  Final Invoice
                </small>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>
                  {order.invoice_number ? (
                    <span style={{ color: "#16a34a" }}>#{order.invoice_number}</span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>Pending</span>
                  )}
                </div>
                {order.invoice_document_id && (
                  <button
                    className="link-btn"
                    style={{ fontSize: 10.5, marginTop: 4, color: "#0284c7", display: "inline-flex", alignItems: "center", gap: 3 }}
                    onClick={() => window.open(downloadDocument(order.invoice_document_id, true), "_blank")}
                  >
                    <Eye size={10} /> View Document
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Stage History & Audit Trail Panel */}
          <section
            className="panel"
            style={{
              background: "#ffffff",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: "16px 18px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
            data-testid="order-stage-history-panel"
          >
            <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Clock size={16} color="#0284c7" />
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Stage History &amp; Audit Trail</h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                    Chronological journey, forward movements &amp; backward stage corrections
                  </p>
                </div>
              </div>
              {currentStage !== "Order Received" && (
                <button
                  className="outline-btn compact"
                  style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11.5, height: 26, padding: "0 8px" }}
                  onClick={() => setMovingBack(true)}
                  data-testid="order-history-move-back-btn"
                >
                  <RotateCcw size={11} /> Move Back
                </button>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <OrderHistoryTrail history={order.history || []} />
            </div>
          </section>
        </div>
      </div>

      {/* Move Back Modal */}
      {movingBack && (
        <MoveBackStageModal
          order={order}
          isOpen={movingBack}
          onClose={() => setMovingBack(false)}
          onSuccess={() => {
            load();
            onChange && onChange();
          }}
          user={user}
        />
      )}

      {/* Edit Modal */}
      {editingOrder && (
        <EditOrderDetailModal
          order={order}
          clients={propClients}
          onClose={() => setEditingOrder(false)}
          onUpdated={() => {
            load();
            onChange && onChange();
          }}
        />
      )}

      {/* Delete Modal */}
      {deletingOrder && (
        <DeleteOrderDetailConfirmationModal
          order={order}
          onClose={() => setDeletingOrder(false)}
          onDeleted={() => {
            onChange && onChange();
            onBack();
          }}
        />
      )}
    </div>
  );
}

function EditOrderDetailModal({ order, clients: initialClients, onClose, onUpdated }) {
  const [clients, setClients] = useState(initialClients || []);
  const [form, setForm] = useState({
    client_id: order.client_id || (order.client_details && order.client_details.client_id) || "",
    product: order.product || "",
    product_code: order.product_code || "",
    quantity: order.quantity || 1,
    unit_price: order.unit_price !== undefined && order.unit_price !== null ? order.unit_price : "",
    required_delivery_date: order.required_delivery_date || "",
    priority: order.priority || "Normal",
    po_number: order.po_number || "",
    po_date: order.po_date || "",
    notes: order.notes || "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!initialClients || initialClients.length === 0) {
      api("get", "/clients")
        .then((res) => {
          const list = (res.data || []).filter((c) => !c.deleted && c.status !== "Inactive" && c.status !== "Archived");
          setClients(list);
        })
        .catch(() => {});
    } else {
      const list = (initialClients || []).filter((c) => !c.deleted && c.status !== "Inactive" && c.status !== "Archived");
      setClients(list);
    }
  }, [initialClients]);

  const clientOptions = useMemo(() => {
    const list = [...clients];
    const currentId = order.client_id || (order.client_details && order.client_details.client_id);
    if (currentId && !list.some((c) => c.client_id === currentId)) {
      list.unshift({
        client_id: currentId,
        company_name: order.client_name || (order.client_details && order.client_details.company_name) || currentId,
        contact_person: order.client_contact_person || (order.client_details && order.client_details.contact_person) || "",
      });
    }
    return list;
  }, [clients, order]);

  useEffect(() => {
    if (!form.client_id) {
      const cid = order.client_id || (order.client_details && order.client_details.client_id) || (clientOptions[0]?.client_id || "");
      if (cid) setForm((f) => ({ ...f, client_id: cid }));
    }
  }, [order, clientOptions, form.client_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      setError("Please select a valid Client");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const unitPriceVal = form.unit_price !== "" && form.unit_price !== null && !isNaN(Number(form.unit_price))
        ? Number(form.unit_price)
        : 0;
      await api("patch", `/orders/${order.order_id}`, {
        ...form,
        quantity: Number(form.quantity),
        unit_price: unitPriceVal,
      });
      toast.success(`Order ${order.order_id} updated`);
      onUpdated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not update order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onSubmit={submit} data-testid="edit-order-detail-form">
        <div className="modal-head">
          <div>
            <div className="small-label">EDIT ORDER</div>
            <h2>Order #{order.order_id}</h2>
            <p className="muted">{order.client_name}</p>
          </div>
          <button type="button" className="icon-btn" data-testid="edit-order-detail-close-button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label style={{ gridColumn: "1 / -1" }}>
            Client
            <select
              data-testid="edit-order-detail-client-select"
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              required
            >
              <option value="" disabled>Select a client…</option>
              {clientOptions.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.company_name || c.name || "Client"}{c.contact_person ? ` (${c.contact_person})` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>Product / service<input data-testid="edit-order-detail-product-input" value={form.product} onChange={(e) => set("product", e.target.value)} required /></label>
          <label>Product code<input data-testid="edit-order-detail-product-code-input" value={form.product_code} onChange={(e) => set("product_code", e.target.value)} /></label>
          <label>PO number<input data-testid="edit-order-detail-po-input" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} /></label>
          <label>PO date<input data-testid="edit-order-detail-po-date-input" type="date" value={form.po_date} min={getTodayDateString()} onChange={(e) => set("po_date", e.target.value)} /></label>
          <label>Quantity<input data-testid="edit-order-detail-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required /></label>
          <label>Unit price (₹)<input data-testid="edit-order-detail-price-input" type="number" min="0" step="0.01" placeholder="Optional" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} /></label>
          <label>Required delivery<input data-testid="edit-order-detail-delivery-date-input" type="date" value={form.required_delivery_date} min={getTodayDateString()} onChange={(e) => set("required_delivery_date", e.target.value)} required /></label>
          <label>Priority<select data-testid="edit-order-detail-priority-select" value={form.priority} onChange={(e) => set("priority", e.target.value)}><option>Normal</option><option>High</option><option>Urgent</option></select></label>
        </div>
        <label className="full">Notes<textarea data-testid="edit-order-detail-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></label>
        {error && <div className="error" data-testid="edit-order-detail-error">{error}</div>}
        <button className="primary-btn wide" data-testid="edit-order-detail-submit-button" disabled={busy}>
          {busy ? "Saving…" : "Save changes"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}

function DeleteOrderDetailConfirmationModal({ order, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api("delete", `/orders/${order.order_id}`);
      toast.success(`Order ${order.order_id} deleted`);
      onDeleted();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Could not delete order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <div
        className="modal"
        style={{ maxWidth: 460 }}
        data-testid="delete-order-detail-confirmation-modal"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
              Delete Order #{order.order_id}
            </h2>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
              Are you sure you want to delete this order?
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid var(--line)",
            borderRadius: 6,
            padding: "12px 14px",
            marginBottom: 20,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div style={{ color: "var(--navy)", fontWeight: 600 }}>{order.product}</div>
          <div style={{ color: "#64748b" }}>{order.client_name} · {(order.quantity || 0).toLocaleString()} units</div>
          <p style={{ margin: "8px 0 0", color: "#94a3b8", fontSize: 12 }}>
            This order will be removed from your active register. All historical client and file links will be maintained.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-delete-order-detail-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleDelete}
            disabled={busy}
            style={{ background: "var(--red)", borderColor: "var(--red)" }}
            data-testid="confirm-delete-order-detail-button"
          >
            {busy ? "Deleting…" : "Delete Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
